import React from "react";
import { MousePointerClick, LayoutPanelTop, Rows3, AlertTriangle } from "lucide-react";
import Toggle from "./Toggle";

const GROUPS = [
  { type: "button", labelKey: "featuresButtons", Icon: MousePointerClick },
  { type: "section", labelKey: "featuresSections", Icon: LayoutPanelTop },
  { type: "tab", labelKey: "featuresTabs", Icon: Rows3 },
];

/**
 * Feature switches for one page, grouped by registry type (button/section/tab).
 * Features are opt-IN: no row in enabledFeatures = the feature is off.
 */
const FeatureToggleList = ({ page, enabledFeatures, onToggleFeature, theme, language, t, disabled = false }) => {
  const features = page?.features || [];
  if (!features.length) return null;

  const textColor = theme === "dark" ? "#cbd5e1" : "#334155";
  const headColor = theme === "dark" ? "#94a3b8" : "#64748b";
  const labelOf = (key) => {
    const f = features.find((x) => x.key === key);
    return f?.labels?.[language] || f?.labels?.en || key;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {GROUPS.map(({ type, labelKey, Icon }) => {
        const group = features.filter((f) => f.type === type);
        if (!group.length) return null;
        return (
          <div key={type}>
            <div style={{
              display: "flex", alignItems: "center", gap: "6px",
              fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em",
              textTransform: "uppercase", color: headColor, marginBottom: "8px",
            }}>
              <Icon size={12} />
              {t[labelKey]}
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))",
              gap: "6px 16px",
            }}>
              {group.map((feature) => {
                const enabled = !!enabledFeatures?.[feature.key];
                // Registry-driven dependency (e.g. Bulk Actions needs at least
                // one action type): warn when it's on but would do nothing.
                const deps = Array.isArray(feature.requiresAny) ? feature.requiresAny : [];
                const unmet = enabled && deps.length > 0 && !deps.some((k) => enabledFeatures?.[k]);
                const hint = feature.hint?.[language] || feature.hint?.en || null;
                return (
                  <label
                    key={feature.key}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: "10px", padding: "7px 10px", borderRadius: "10px",
                      ...(hint || unmet ? { gridColumn: "1 / -1" } : {}),
                      cursor: "pointer", userSelect: "none",
                      transition: "background 0.15s ease",
                      background: enabled
                        ? (theme === "dark" ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.06)")
                        : "transparent",
                    }}
                  >
                    <span style={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                      <span style={{ fontSize: "13px", color: textColor, fontWeight: enabled ? 600 : 400 }}>
                        {feature.labels?.[language] || feature.labels?.en || feature.key}
                      </span>
                      {hint && (
                        <span style={{ fontSize: "11.5px", color: headColor, lineHeight: 1.45 }}>{hint}</span>
                      )}
                      {unmet && (
                        <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11.5px", color: "#d97706", fontWeight: 600 }}>
                          <AlertTriangle size={12} style={{ flexShrink: 0 }} />
                          {language === "ar"
                            ? `لن يظهر الزر حتى تفعيل واحد على الأقل من: ${deps.map(labelOf).join("، ")}`
                            : `The button stays hidden until at least one of these is enabled: ${deps.map(labelOf).join(", ")}`}
                        </span>
                      )}
                    </span>
                    <Toggle
                      theme={theme}
                      checked={enabled}
                      onChange={(next) => onToggleFeature(page.key, feature.key, next)}
                      disabled={disabled}
                    />
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FeatureToggleList;
